import type { ParameterDefinition } from "./types";

export class ExpressionError extends Error {
  constructor(message: string, public readonly position?: number) {
    super(position === undefined ? message : `${message} at character ${position + 1}`);
    this.name = "ExpressionError";
  }
}

type Token =
  | { type: "number"; value: number; position: number }
  | { type: "identifier"; value: string; position: number }
  | { type: "operator"; value: "+" | "-" | "*" | "/"; position: number }
  | { type: "left" | "right" | "comma"; position: number }
  | { type: "eof"; position: number };

function tokenize(source: string): Token[] {
  const input = source.trim().replace(/(\d(?:[\d.]*\d)?|\d)\s*(mm|deg)\b/gi, "$1");
  const tokens: Token[] = [];
  let index = 0;

  while (index < input.length) {
    const char = input[index];
    if (char === undefined) break;
    if (/\s/.test(char)) {
      index += 1;
      continue;
    }
    if (/[0-9.]/.test(char)) {
      const start = index;
      let seenDot = false;
      while (index < input.length) {
        const next = input[index];
        if (next === ".") {
          if (seenDot) break;
          seenDot = true;
          index += 1;
        } else if (next !== undefined && /[0-9]/.test(next)) {
          index += 1;
        } else {
          break;
        }
      }
      if (input[index] === "e" || input[index] === "E") {
        index += 1;
        if (input[index] === "+" || input[index] === "-") index += 1;
        while (input[index] !== undefined && /[0-9]/.test(input[index] ?? "")) index += 1;
      }
      const raw = input.slice(start, index);
      const value = Number(raw);
      if (!Number.isFinite(value)) throw new ExpressionError(`Invalid number “${raw}”`, start);
      tokens.push({ type: "number", value, position: start });
      continue;
    }
    if (/[A-Za-z_]/.test(char)) {
      const start = index;
      index += 1;
      while (input[index] !== undefined && /[A-Za-z0-9_]/.test(input[index] ?? "")) index += 1;
      tokens.push({ type: "identifier", value: input.slice(start, index), position: start });
      continue;
    }
    if (char === "+" || char === "-" || char === "*" || char === "/") {
      tokens.push({ type: "operator", value: char, position: index });
      index += 1;
      continue;
    }
    if (char === "(") tokens.push({ type: "left", position: index });
    else if (char === ")") tokens.push({ type: "right", position: index });
    else if (char === ",") tokens.push({ type: "comma", position: index });
    else throw new ExpressionError(`Unexpected character “${char}”`, index);
    index += 1;
  }
  tokens.push({ type: "eof", position: input.length });
  return tokens;
}

class Parser {
  private cursor = 0;

  constructor(
    private readonly tokens: Token[],
    private readonly variables: Readonly<Record<string, number>>
  ) {}

  parse(): number {
    const result = this.parseAdditive();
    const tail = this.peek();
    if (tail.type !== "eof") throw new ExpressionError("Unexpected trailing input", tail.position);
    if (!Number.isFinite(result)) throw new ExpressionError("Expression did not produce a finite number");
    return result;
  }

  private peek(): Token {
    return this.tokens[this.cursor] ?? { type: "eof", position: 0 };
  }

  private consume(): Token {
    const token = this.peek();
    this.cursor += 1;
    return token;
  }

  private parseAdditive(): number {
    let value = this.parseMultiplicative();
    while (true) {
      const token = this.peek();
      if (token.type !== "operator" || (token.value !== "+" && token.value !== "-")) return value;
      this.consume();
      const right = this.parseMultiplicative();
      value = token.value === "+" ? value + right : value - right;
    }
  }

  private parseMultiplicative(): number {
    let value = this.parseUnary();
    while (true) {
      const token = this.peek();
      if (token.type !== "operator" || (token.value !== "*" && token.value !== "/")) return value;
      this.consume();
      const right = this.parseUnary();
      if (token.value === "/" && Math.abs(right) < Number.EPSILON) throw new ExpressionError("Division by zero", token.position);
      value = token.value === "*" ? value * right : value / right;
    }
  }

  private parseUnary(): number {
    const token = this.peek();
    if (token.type === "operator" && (token.value === "+" || token.value === "-")) {
      this.consume();
      const value = this.parseUnary();
      return token.value === "-" ? -value : value;
    }
    return this.parsePrimary();
  }

  private parsePrimary(): number {
    const token = this.consume();
    if (token.type === "number") return token.value;
    if (token.type === "left") {
      const value = this.parseAdditive();
      const close = this.consume();
      if (close.type !== "right") throw new ExpressionError("Expected closing parenthesis", close.position);
      return value;
    }
    if (token.type === "identifier") {
      if (this.peek().type === "left") return this.parseFunction(token.value, token.position);
      const value = this.variables[token.value];
      if (value === undefined) throw new ExpressionError(`Unknown parameter “${token.value}”`, token.position);
      return value;
    }
    throw new ExpressionError("Expected a number, parameter or parenthesis", token.position);
  }

  private parseFunction(name: string, position: number): number {
    this.consume();
    const args: number[] = [];
    if (this.peek().type !== "right") {
      while (true) {
        args.push(this.parseAdditive());
        if (this.peek().type !== "comma") break;
        this.consume();
      }
    }
    const close = this.consume();
    if (close.type !== "right") throw new ExpressionError("Expected closing parenthesis", close.position);

    switch (name) {
      case "min":
        if (args.length < 1) throw new ExpressionError("min requires at least one argument", position);
        return Math.min(...args);
      case "max":
        if (args.length < 1) throw new ExpressionError("max requires at least one argument", position);
        return Math.max(...args);
      case "abs":
        if (args.length !== 1) throw new ExpressionError("abs requires one argument", position);
        return Math.abs(args[0] ?? 0);
      case "round":
        if (args.length !== 1 && args.length !== 2) throw new ExpressionError("round requires one or two arguments", position);
        if (args.length === 1) return Math.round(args[0] ?? 0);
        {
          const digits = Math.trunc(args[1] ?? 0);
          const factor = 10 ** digits;
          return Math.round((args[0] ?? 0) * factor) / factor;
        }
      default:
        throw new ExpressionError(`Unknown function “${name}”`, position);
    }
  }
}

export function evaluateExpression(expression: string, variables: Readonly<Record<string, number>> = {}): number {
  if (!expression.trim()) throw new ExpressionError("Value cannot be empty");
  return new Parser(tokenize(expression), variables).parse();
}

export function resolveParameters(parameters: Readonly<Record<string, ParameterDefinition>>): Record<string, number> {
  const byName = new Map<string, ParameterDefinition>();
  for (const parameter of Object.values(parameters)) {
    if (byName.has(parameter.name)) throw new ExpressionError(`Duplicate parameter name “${parameter.name}”`);
    byName.set(parameter.name, parameter);
  }

  const resolved: Record<string, number> = {};
  const resolving = new Set<string>();

  const resolve = (name: string): number => {
    const existing = resolved[name];
    if (existing !== undefined) return existing;
    const parameter = byName.get(name);
    if (!parameter) throw new ExpressionError(`Unknown parameter “${name}”`);
    if (resolving.has(name)) throw new ExpressionError(`Circular parameter dependency involving “${name}”`);
    resolving.add(name);

    const variables = new Proxy(resolved, {
      get(target, property: string | symbol) {
        if (typeof property !== "string") return undefined;
        if (target[property] !== undefined) return target[property];
        return resolve(property);
      },
      has(_target, property) {
        return typeof property === "string" && byName.has(property);
      }
    });

    let value: number;
    try {
      value = evaluateExpression(parameter.value, variables as Record<string, number>);
    } finally {
      resolving.delete(name);
    }
    if (parameter.type === "integer") value = Math.round(value);
    if (parameter.minimum !== undefined && value < parameter.minimum) {
      throw new ExpressionError(`${parameter.label} must be at least ${parameter.minimum}`);
    }
    if (parameter.maximum !== undefined && value > parameter.maximum) {
      throw new ExpressionError(`${parameter.label} must be at most ${parameter.maximum}`);
    }
    resolved[name] = value;
    return value;
  };

  for (const name of byName.keys()) resolve(name);
  return resolved;
}

export function resolveNumericInput(input: string, variables: Readonly<Record<string, number>>): number {
  return evaluateExpression(input, variables);
}
